import React, { useState, useEffect } from "react";
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { getOrderHistory } from "../redux/OrderCRUD";
import { AppPagination } from '@/__core/components';
import DataTableModule from '@/__core/layout/module/DataTableModule';

interface OrderHistoryProps {
  orderId?: string;
  additionalFees?: Array<{ id: string; name: string; amount: number }>;
}

interface HistoryItem {
  id: string;
  history_id: string;
  history_date: string;
  history_type: string;
  history_type_display: string;
  history_user: string | null;
  history_user_email: string | null;
  history_user_name?: string | null;
  
  // Order fields (có khi type = 'order')
  code?: string;
  customer_name?: string;
  sales_channel_name?: string;
  sales_employee_name?: string;
  status_name?: string;
  sale_date?: string;
  delivery_date?: string;
  delivery_phone?: string;
  delivery_address_line?: string;
  survey_employee_name?: string;
  tech_cnc_employee_name?: string;
  notes?: string;
  survey_fee_amount?: string;
  shipping_installation_fee_amount?: string;
  is_deleted?: boolean;
  deleted_at?: string | null;
  deleted_by_name?: string | null;
  restored_at?: string | null;
  restored_by_name?: string | null;
  
  // Transaction fields (có khi type = 'transaction')
  transaction_type?: string;
  transaction_amount?: {
    actual: number;
    total: number;
  };
  transaction_wallet?: string;
  transaction_status?: {
    id?: string;
    name?: string;
    code?: string;
  } | string | null;
  transaction_note?: string;
}

const OrderHistory: React.FC<OrderHistoryProps> = ({ orderId, additionalFees = [] }) => {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [pagination, setPagination] = useState({
    count: 0,
    totalPages: 1,
  });

  useEffect(() => {
    if (orderId) {
      fetchOrderHistory();
    }
  }, [orderId, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [orderId]);

  const fetchOrderHistory = async () => {
    if (!orderId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getOrderHistory(orderId, {
        page,
        page_size: pageSize,
      });
      setHistory(response.results || []);
      setPagination({
        count: Number(response.count || 0),
        totalPages: Math.max(1, Math.ceil(Number(response.count || 0) / pageSize)),
      });
    } catch (err: any) {
      console.error("Error fetching order history:", err);
      setError(err.message || "Không thể tải lịch sử đơn hàng");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatCurrency = (amount: string) => {
    return new Intl.NumberFormat('vi-VN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseFloat(amount));
  };

  const isTransactionHistoryItem = (item: HistoryItem) => {
    return String(item.history_id || '').startsWith('transaction_');
  };

  const isOrderHistoryItem = (item: HistoryItem) => {
    return String(item.history_id || '').startsWith('order_');
  };

  const getTransactionStatusName = (transactionStatus: HistoryItem['transaction_status']) => {
    if (!transactionStatus) {
      return '';
    }

    if (typeof transactionStatus === 'string') {
      return transactionStatus;
    }

    return transactionStatus.name || transactionStatus.code || '';
  };

  const getTransactionStatusColor = (transactionStatus: HistoryItem['transaction_status']) => {
    const normalizedStatus = String(getTransactionStatusName(transactionStatus) || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

    if (["approved", "duyet", "da duyet"].includes(normalizedStatus)) {
      return '#059669';
    }

    if (normalizedStatus) {
      return '#d97706';
    }

    return '#9ca3af';
  };

  const findComparablePreviousItem = (item: HistoryItem) => {
    const currentIndex = history.findIndex((historyItem) => historyItem.history_id === item.history_id);
    if (currentIndex < 0) {
      return null;
    }

    const currentIsTransaction = isTransactionHistoryItem(item);
    const currentIsOrder = isOrderHistoryItem(item);

    for (let index = currentIndex + 1; index < history.length; index += 1) {
      const candidate = history[index];
      if (!candidate) {
        continue;
      }

      if (currentIsTransaction && isTransactionHistoryItem(candidate)) {
        return candidate;
      }

      if (currentIsOrder && isOrderHistoryItem(candidate)) {
        return candidate;
      }
    }

    return null;
  };

  const getComparableValue = (item: HistoryItem, field: string) => {
    const value = item[field as keyof HistoryItem];

    if (field === 'transaction_status') {
      return getTransactionStatusName(item.transaction_status);
    }

    if (value && typeof value === 'object') {
      return JSON.stringify(value);
    }

    return value;
  };


  // Function to check if a field has changed
  const isFieldChanged = (item: HistoryItem, field: string): boolean => {
    if (item.history_type !== '~') {
      return false;
    }

    const previous = findComparablePreviousItem(item);
    if (!previous) {
      return false;
    }
    
    return getComparableValue(item, field) !== getComparableValue(previous, field);
  };

  // Function to check if a row has any changes
  const hasRowChanges = (item: HistoryItem): boolean => {
    if (item.history_type !== '~') {
      return false;
    }
    
    const fieldsToCheck = [
      'code', 'customer_name', 'sales_channel_name', 'sales_employee_name',
      'sale_date', 'delivery_date', 'delivery_phone', 'delivery_address_line',
      'survey_employee_name', 'tech_cnc_employee_name', 'status_name',
      'notes', 'survey_fee_amount', 'shipping_installation_fee_amount',
      'is_deleted', 'deleted_at', 'deleted_by_name', 'restored_by_name',
      'transaction_type', 'transaction_wallet', 'transaction_amount',
      'transaction_status', 'transaction_note'
    ];
    
    return fieldsToCheck.some(field => isFieldChanged(item, field));
  };

  // Create columns for DataTableModule
  const columns = [
    {
      id: 'history_date',
      label: 'Thời gian',
      width: '150px',
      render: (value: any, item: HistoryItem) => (
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {formatDate(item.history_date)}
        </Typography>
      ),
    },
    {
      id: 'history_type_display',
      label: 'Hành động',
      width: '150px',
      render: (value: any, item: HistoryItem) => (
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
          {item.history_type_display}
        </Typography>
      ),
    },
    {
      id: 'history_user_email',
      label: 'Người thực hiện',
      width: '150px',
      render: (value: any, item: HistoryItem) => (
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {item.history_user_name || item.history_user_email || 'Hệ thống'}
        </Typography>
      ),
    },
    {
      id: 'code',
      label: 'Mã đơn hàng',
      width: '160px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'code') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.code || '-'}
        </Typography>
      ),
    },
    {
      id: 'customer_name',
      label: 'Khách hàng',
      width: '150px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'customer_name') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.customer_name || '-'}
        </Typography>
      ),
    },
    {
      id: 'sales_channel_name',
      label: 'Kênh bán',
      width: '120px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'sales_channel_name') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.sales_channel_name || '-'}
        </Typography>
      ),
    },
    {
      id: 'sales_employee_name',
      label: 'NV bán hàng',
      width: '150px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'sales_employee_name') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.sales_employee_name || '-'}
        </Typography>
      ),
    },
    {
      id: 'sale_date',
      label: 'Ngày bán',
      width: '100px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'sale_date') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.sale_date || '-'}
        </Typography>
      ),
    },
    {
      id: 'delivery_date',
      label: 'Ngày giao',
      width: '110px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'delivery_date') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.delivery_date || '-'}
        </Typography>
      ),
    },
    {
      id: 'delivery_phone',
      label: 'SĐT giao hàng',
      width: '170px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'delivery_phone') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.delivery_phone || '-'}
        </Typography>
      ),
    },
    {
      id: 'delivery_address_line',
      label: 'Địa chỉ giao hàng',
      width: '200px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'delivery_address_line') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.delivery_address_line || '-'}
        </Typography>
      ),
    },
    {
      id: 'survey_employee_name',
      label: 'NV khảo sát',
      width: '150px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'survey_employee_name') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.survey_employee_name || '-'}
        </Typography>
      ),
    },
    {
      id: 'tech_cnc_employee_name',
      label: 'NV sản xuất',
      width: '150px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'tech_cnc_employee_name') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.tech_cnc_employee_name || '-'}
        </Typography>
      ),
    },
    {
      id: 'status_name',
      label: 'Trạng thái',
      width: '120px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 500,
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'status_name') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.status_name || '-'}
        </Typography>
      ),
    },
    {
      id: 'is_deleted',
      label: 'Đã xóa',
      width: '80px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            fontWeight: 500,
            color: item.is_deleted ? '#ef4444' : '#10b981',
            backgroundColor: isFieldChanged(item, 'is_deleted') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.is_deleted !== undefined ? (item.is_deleted ? 'Có' : 'Không') : '-'}
        </Typography>
      ),
    },
    {
      id: 'deleted_at',
      label: 'Ngày xóa',
      width: '150px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'deleted_at') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.deleted_at ? formatDate(item.deleted_at) : '-'}
        </Typography>
      ),
    },
    {
      id: 'deleted_by_name',
      label: 'Người xóa',
      width: '150px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'deleted_by_name') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.deleted_by_name || '-'}
        </Typography>
      ),
    },
    {
      id: 'restored_by_name',
      label: 'Người phục hồi',
      width: '150px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'restored_by_name') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.restored_by_name || '-'}
        </Typography>
      ),
    },
    {
      id: 'notes',
      label: 'Ghi chú',
      width: '200px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'notes') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px',
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={item.notes}
        >
          {item.notes || '-'}
        </Typography>
      ),
    },
    {
      id: 'survey_fee_amount',
      label: 'Phí khảo sát',
      width: '120px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'survey_fee_amount') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.survey_fee_amount ? formatCurrency(item.survey_fee_amount) : '-'}
        </Typography>
      ),
    },
    {
      id: 'shipping_installation_fee_amount',
      label: 'Phí vận chuyển, lắp đặt',
      width: '200px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            backgroundColor: isFieldChanged(item, 'shipping_installation_fee_amount') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.shipping_installation_fee_amount ? formatCurrency(item.shipping_installation_fee_amount) : '-'}
        </Typography>
      ),
    },
    {
      id: 'transaction_type',
      label: 'Loại giao dịch',
      width: '150px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            fontWeight: item.transaction_type ? 600 : 400,
            color: item.transaction_type ? '#2563eb' : '#9ca3af',
            backgroundColor: isFieldChanged(item, 'transaction_type') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.transaction_type || '-'}
        </Typography>
      ),
    },
    {
      id: 'transaction_wallet',
      label: 'Ví',
      width: '120px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            color: item.transaction_wallet ? '#6b7280' : '#9ca3af',
            backgroundColor: isFieldChanged(item, 'transaction_wallet') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.transaction_wallet || '-'}
        </Typography>
      ),
    },
    {
      id: 'transaction_amount_actual',
      label: 'Số tiền thực tế',
      width: '150px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            fontWeight: item.transaction_amount ? 600 : 400,
            color: item.transaction_amount ? '#059669' : '#9ca3af',
            backgroundColor: isFieldChanged(item, 'transaction_amount') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.transaction_amount ? formatCurrency(item.transaction_amount.actual.toString()) : '-'}
        </Typography>
      ),
    },
    {
      id: 'transaction_amount_total',
      label: 'Tổng cộng',
      width: '150px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            fontWeight: item.transaction_amount ? 600 : 400,
            color: item.transaction_amount ? '#2563eb' : '#9ca3af',
            backgroundColor: isFieldChanged(item, 'transaction_amount') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {item.transaction_amount ? formatCurrency(item.transaction_amount.total.toString()) : '-'}
        </Typography>
      ),
    },
    {
      id: 'transaction_status',
      label: 'Trạng thái GD',
      width: '140px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            fontWeight: item.transaction_status ? 500 : 400,
            color: getTransactionStatusColor(item.transaction_status),
            backgroundColor: isFieldChanged(item, 'transaction_status') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px'
          }}
        >
          {getTransactionStatusName(item.transaction_status) || '-'}
        </Typography>
      ),
    },
    {
      id: 'transaction_note',
      label: 'Ghi chú GD',
      width: '200px',
      render: (value: any, item: HistoryItem) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.875rem',
            color: item.transaction_note ? '#6b7280' : '#9ca3af',
            backgroundColor: isFieldChanged(item, 'transaction_note') ? '#ffeb3b' : 'transparent',
            padding: '2px 4px',
            borderRadius: '4px',
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontStyle: item.transaction_note ? 'italic' : 'normal'
          }}
          title={item.transaction_note}
        >
          {item.transaction_note || '-'}
        </Typography>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Đang tải lịch sử...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!orderId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Lịch sử đơn hàng</Typography>
        <Alert severity="info">Vui lòng chọn đơn hàng để xem lịch sử</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0, overflow: 'hidden' }}>
      {history.length === 0 ? (
        <Alert severity="info">Chưa có lịch sử nào cho đơn hàng này</Alert>
      ) : (
        <>
          <DataTableModule
            data={history}
            columns={columns}
            getRowId={(item: HistoryItem) => item.history_id.toString()}
            onRowSelect={() => {}}
            onSelectAll={() => {}}
            isRowSelected={() => false}
            areAllRowsSelected={false}
            areSomeRowsSelected={false}
            leftFilterWidth={13}
            showCheckbox={false}
            getRowClassName={(item: HistoryItem) => hasRowChanges(item) ? 'changed-row' : ''}
          />
          <AppPagination
            count={pagination.count}
            page={page}
            pageSize={pageSize}
            totalPages={pagination.totalPages}
            itemLabel='lịch sử'
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onPageSizeChange={(nextPageSize) => {
              setPageSize(nextPageSize);
              setPage(1);
            }}
          />
        </>
      )}
    </Box>
  );
};

export default OrderHistory;
